import { type ReactNode, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '@helios/auth-firebase-web';
import {
  computeGlobalPackageSummary,
  filterGlobalPackages,
  qPatientPackagesGlobal,
  qRedeemedTodayCount,
  sortGlobalPackages,
  type GlobalPackageFilters,
  type GlobalPatientPackageRow,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { type ShellOutletContext } from '../../shell/shared';
import {
  FilterDropdown,
  InventoryListToolbar,
  InventoryPageShell,
  InventoryRow,
  InventoryTable,
} from './_shared';
import { PatientPackagesDrawer } from './PatientPackagesDrawer';

// PRD.263 — Global Patient Packages operations page. Reuses the PRD.262 drawer
// for viewing/redeeming; no second redemption UI. Batch selection + stock
// deduction stay in Pharmacy / Product Release.

const PAYMENT_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'partially_paid', label: 'Partially Paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'voided', label: 'Voided' },
] as const;

const REDEMPTION_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'pending_payment', label: 'Pending Payment' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'fully_redeemed', label: 'Fully Redeemed' },
  { value: 'voided', label: 'Voided' },
] as const;

const QUICK_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'expiring', label: 'Expiring Soon' },
  { value: 'has_remaining', label: 'Has Remaining' },
  { value: 'pending_release', label: 'Pending Release' },
] as const;

const COLUMNS = [
  { label: 'Patient', width: '1.4fr' },
  { label: 'Package', width: '1.4fr' },
  { label: 'Payment', width: '120px' },
  { label: 'Redemption', width: '130px' },
  { label: 'Paid / Total', width: '1.1fr', align: 'right' as const },
  { label: 'Balance', width: '1fr', align: 'right' as const },
  { label: 'Valid Until', width: '110px' },
  { label: 'Remaining', width: '100px', align: 'right' as const },
  { label: 'Last Redeemed', width: '110px' },
  { label: 'Actions', width: '170px', align: 'right' as const },
];

interface DrawerState {
  patientId: string;
  patientName: string | null;
  packageId: string;
}

export function GlobalPatientPackagesPage() {
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {
    collapsed: false,
  };
  const navigate = useNavigate();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const roles = orgId ? (claims?.orgs?.[orgId]?.roles ?? []) : [];
  const isAdmin = roles.includes('admin');
  const fmt = useFormatMoney();
  const today = new Date().toISOString().slice(0, 10);

  const { data: rows = [] } = usePowerSyncQuery<GlobalPatientPackageRow>(
    qPatientPackagesGlobal(orgId),
  );
  const { data: redeemedTodayRows = [] } = usePowerSyncQuery<{ cnt: number }>(
    qRedeemedTodayCount(today),
  );
  const redeemedToday = redeemedTodayRows[0]?.cnt ?? 0;

  const [search, setSearch] = useState('');
  const [paymentFilter, setPaymentFilter] =
    useState<(typeof PAYMENT_OPTIONS)[number]['value']>('all');
  const [redemptionFilter, setRedemptionFilter] =
    useState<(typeof REDEMPTION_OPTIONS)[number]['value']>('all');
  const [quickFilter, setQuickFilter] = useState<(typeof QUICK_OPTIONS)[number]['value']>('all');
  const [drawer, setDrawer] = useState<DrawerState | null>(null);

  const summary = useMemo(() => computeGlobalPackageSummary(rows, today), [rows, today]);

  const visible = useMemo(() => {
    const filters: GlobalPackageFilters = {
      paymentStatus: paymentFilter === 'all' ? undefined : [paymentFilter],
      redemptionStatus: redemptionFilter === 'all' ? undefined : [redemptionFilter],
      expiringSoon: quickFilter === 'expiring',
      hasRemainingBenefits: quickFilter === 'has_remaining',
      hasPendingRelease: quickFilter === 'pending_release',
    };
    const structured = filterGlobalPackages(rows, filters, today);
    const q = search.trim().toLowerCase();
    const searched = q
      ? structured.filter((r) =>
          `${r.patient_name ?? ''} ${r.patient_number ?? ''} ${r.package_name ?? ''} ${r.package_code ?? ''}`
            .toLowerCase()
            .includes(q),
        )
      : structured;
    return sortGlobalPackages(searched);
  }, [rows, search, paymentFilter, redemptionFilter, quickFilter, today]);

  const hasFilters =
    !!search.trim() ||
    paymentFilter !== 'all' ||
    redemptionFilter !== 'all' ||
    quickFilter !== 'all';

  return (
    <InventoryPageShell
      title="Patient Packages"
      subtitle="Manage package balances, payment status, redemptions, and pending releases."
      collapsed={collapsed}
      onToggleSidebar={onToggleSidebar}
    >
      {/* Summary cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
          gap: 'var(--sp-3)',
          padding: 'var(--sp-4) var(--sp-6) 0',
        }}
      >
        <SummaryCard
          label="Active Packages"
          value={String(summary.activePackages)}
          tone="success"
        />
        <SummaryCard label="Partially Paid" value={String(summary.partiallyPaid)} tone="warning" />
        <SummaryCard label="Expiring Soon" value={String(summary.expiringSoon)} tone="warning" />
        <SummaryCard label="Redeemed Today" value={String(redeemedToday)} tone="info" />
        <SummaryCard label="Pending Release" value={String(summary.pendingRelease)} tone="info" />
      </div>

      <InventoryListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search patient, MRN, package name or code..."
        filters={
          <>
            <FilterDropdown
              label="Payment"
              value={paymentFilter}
              options={PAYMENT_OPTIONS}
              onChange={setPaymentFilter}
            />
            <FilterDropdown
              label="Redemption"
              value={redemptionFilter}
              options={REDEMPTION_OPTIONS}
              onChange={setRedemptionFilter}
            />
            <FilterDropdown
              label="Show"
              value={quickFilter}
              options={QUICK_OPTIONS}
              onChange={setQuickFilter}
            />
          </>
        }
      />

      <div style={{ height: 'var(--sp-4)', flexShrink: 0 }} />

      <InventoryTable
        columns={COLUMNS}
        empty={
          visible.length === 0
            ? {
                show: true,
                title: hasFilters
                  ? 'No patient packages match these filters.'
                  : 'No patient packages yet.',
                hint: hasFilters
                  ? 'Try clearing a filter.'
                  : 'Sell a package from an invoice to create a patient package.',
              }
            : undefined
        }
      >
        {visible.map((r) => {
          const redeemable =
            r.redemption_status === 'active' &&
            (!r.valid_until || r.valid_until.slice(0, 10) >= today) &&
            (r.remaining_quantity ?? 0) > 0;
          const openDrawer = () =>
            r.patient_id &&
            setDrawer({
              patientId: r.patient_id,
              patientName: r.patient_name,
              packageId: r.patient_package_id,
            });
          return (
            <InventoryRow
              key={r.patient_package_id}
              columns={COLUMNS}
              clickable
              onClick={openDrawer}
            >
              {/* Patient */}
              <TwoLine primary={r.patient_name ?? '(Unknown)'} secondary={r.patient_number} />
              {/* Package */}
              <TwoLine primary={r.package_name ?? 'Package'} secondary={r.package_code} />
              {/* Payment */}
              <span>
                <StatusPill {...paymentMeta(r.payment_status)} />
                {r.has_pending_release === 1 && <PendingReleasePill />}
              </span>
              {/* Redemption */}
              <StatusPill {...redemptionMeta(r.redemption_status)} />
              {/* Paid / Total */}
              <Cell align="right">
                {fmt((r.paid_cents ?? 0) / 100)} / {fmt((r.grand_cents ?? 0) / 100)}
              </Cell>
              {/* Balance */}
              <Cell align="right" emphasize>
                {fmt(Math.max(0, r.balance_cents ?? 0) / 100)}
              </Cell>
              {/* Valid Until */}
              <Cell muted>{r.valid_until ? r.valid_until.slice(0, 10) : 'No expiry'}</Cell>
              {/* Remaining */}
              <Cell align="right">
                {fmtQty(r.remaining_quantity)} / {fmtQty(r.total_quantity)}
              </Cell>
              {/* Last Redeemed */}
              <Cell muted>{r.last_redeemed_at ? r.last_redeemed_at.slice(0, 10) : '—'}</Cell>
              {/* Actions */}
              <span
                style={{ display: 'flex', gap: 'var(--sp-1)', justifyContent: 'flex-end' }}
                onClick={(e) => e.stopPropagation()}
              >
                <RowLink label="View" onClick={openDrawer} />
                {redeemable && <RowLink label="Redeem" onClick={openDrawer} primary />}
                <RowLink
                  label="Patient"
                  onClick={() => r.patient_id && navigate(`/patient/${r.patient_id}`)}
                />
              </span>
            </InventoryRow>
          );
        })}
      </InventoryTable>

      {drawer && (
        <PatientPackagesDrawer
          open
          onOpenChange={(o) => {
            if (!o) setDrawer(null);
          }}
          patientId={drawer.patientId}
          patientName={drawer.patientName}
          initialPatientPackageId={drawer.packageId}
          userId={user?.uid ?? null}
          isAdmin={isAdmin}
          onOpenPatient={() => {
            const id = drawer.patientId;
            setDrawer(null);
            navigate(`/patient/${id}`);
          }}
        />
      )}
    </InventoryPageShell>
  );
}

// ─── Row cell helpers ───

function TwoLine({ primary, secondary }: { primary: string; secondary: string | null }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {primary}
      </span>
      {secondary && (
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          {secondary}
        </span>
      )}
    </div>
  );
}

function Cell({
  children,
  align = 'left',
  muted = false,
  emphasize = false,
}: {
  children: ReactNode;
  align?: 'left' | 'right';
  muted?: boolean;
  emphasize?: boolean;
}) {
  return (
    <span
      style={{
        fontSize: 'var(--font-size-sm)',
        fontWeight: emphasize ? 'var(--font-weight-semi)' : 'var(--font-weight-regular, 400)',
        color: muted ? 'var(--text-muted)' : 'var(--text)',
        textAlign: align,
      }}
    >
      {children}
    </span>
  );
}

function RowLink({
  label,
  onClick,
  primary = false,
}: {
  label: string;
  onClick: () => void;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: 'none',
        background: 'transparent',
        color: primary ? 'var(--color-primary)' : 'var(--text-muted)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: primary ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
        cursor: 'pointer',
        padding: '2px 4px',
        fontFamily: 'var(--font-family)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}

function StatusPill({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 'var(--radius-full, 9999px)',
        background: bg,
        color,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

function PendingReleasePill() {
  return (
    <span
      style={{
        display: 'inline-block',
        marginLeft: 4,
        fontSize: 9,
        fontWeight: 700,
        padding: '1px 6px',
        borderRadius: 'var(--radius-full, 9999px)',
        background: 'var(--color-warning-bg)',
        color: 'var(--color-warning-text)',
        whiteSpace: 'nowrap',
      }}
      title="A redeemed medicine/product is waiting for Pharmacy or Product Release."
    >
      Pending release
    </span>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'info' | 'warning' | 'success';
}) {
  const palette = {
    info: { bg: 'var(--color-info-bg, #dbeafe)', color: 'var(--color-info-text, #1e40af)' },
    warning: { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' },
    success: { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' },
  }[tone];
  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        padding: 'var(--sp-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontWeight: 'var(--font-weight-semi)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          alignSelf: 'flex-start',
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          padding: '2px 10px',
          borderRadius: 'var(--radius-full, 9999px)',
          background: palette.bg,
          color: palette.color,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function paymentMeta(status: string | null): { label: string; bg: string; color: string } {
  switch (status) {
    case 'paid':
      return { label: 'Paid', bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' };
    case 'partially_paid':
      return {
        label: 'Partial',
        bg: 'var(--color-warning-bg)',
        color: 'var(--color-warning-text)',
      };
    case 'voided':
      return { label: 'Voided', bg: 'var(--bg-surface-2)', color: 'var(--text-muted)' };
    default:
      return { label: 'Unpaid', bg: 'var(--bg-surface-2)', color: 'var(--text-muted)' };
  }
}

function redemptionMeta(status: string | null): { label: string; bg: string; color: string } {
  switch (status) {
    case 'active':
      return { label: 'Active', bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' };
    case 'suspended':
      return {
        label: 'Suspended',
        bg: 'var(--color-warning-bg)',
        color: 'var(--color-warning-text)',
      };
    case 'fully_redeemed':
      return {
        label: 'Fully Redeemed',
        bg: 'var(--color-info-bg, #dbeafe)',
        color: 'var(--color-info-text, #1e40af)',
      };
    case 'voided':
      return { label: 'Voided', bg: 'var(--bg-surface-2)', color: 'var(--text-muted)' };
    default:
      return {
        label: 'Pending',
        bg: 'var(--color-info-bg, #dbeafe)',
        color: 'var(--color-info-text, #1e40af)',
      };
  }
}

function fmtQty(n: number | null): string {
  if (n === null || n === undefined) return '0';
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(3)));
}
